import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { skyJobURL } from './skyEndpoints'
import type {
  AutoCoderEntity,
  DateRange,
  EntityDetailResponse,
  EntityPatchRequest,
  EntityQuery,
} from '../../types/gotypes'

/**
 * The post-data editor's three requests (#1196): find a post, read what it
 * currently says, correct it.
 *
 * WHY THESE ARE HERE AND NOT IN useEntityBrowser. The AutoCoder Data tab
 * already browses entities, and it does so for a customer and job the user
 * picked in the job selector. This page is Sky's, on a fixed customer/job, and
 * its first question is "which post is this link", which the browser has no
 * concept of. Sharing the browser's hook would mean teaching it a Sky scope and
 * a URL lookup it does not want; a second small reader beside it costs less than
 * a shared one that serves neither screen properly.
 *
 * All three go through `skyJobURL` — see the long note there for why the
 * job-wide mount is right for this screen and wrong for a dashboard.
 */

/** `POST /entities/query` — mirrored here, as `useEntityBrowser` mirrors it. */
interface EntityListResponse {
  entities: AutoCoderEntity[]
  /** Matches BEFORE paging, so the pager knows how many pages there are. */
  totalCount: number
}

export interface EntitySearchOptions {
  /**
   * A pasted permalink. The whole reason this page has a lookup rather than a
   * search box: it is two indexed equality arms server-side, and it survives
   * share-sheet junk (tracking parameters, missing scheme, `www.`, a trailing
   * slash) which a substring search does not.
   */
  url?: string
  /** Sprinklr campaign ids. Empty means no constraint, as everywhere else. */
  campaigns?: string[]
  publishedDateRange?: DateRange | null
  page?: number
  rowsPerPage?: number
  enabled?: boolean
}

export interface EntitySearchResult {
  entities: AutoCoderEntity[]
  totalCount: number
  isLoading: boolean
  isFetching: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Build the query half of the body.
 *
 * 🔴 EVERY EMPTY FILTER IS OMITTED RATHER THAN SENT EMPTY. An empty `url` or an
 * empty `campaigns` adds no predicate server-side, so sending them changes
 * nothing about the answer — but it changes the CACHE KEY, which makes "I
 * cleared the box" a different entry from "I never typed in it" and refetches
 * the same rows under a second name.
 */
export function buildEntitySearchQuery(options: EntitySearchOptions): EntityQuery {
  const entity: EntityQuery = {}
  const url = (options.url ?? '').trim()
  if (url !== '') entity.url = url
  if (options.campaigns && options.campaigns.length > 0) entity.campaigns = options.campaigns
  if (options.publishedDateRange) entity.publishedDateRange = options.publishedDateRange
  return entity
}

/** One page of matching posts. */
export function useEntitySearch(options: EntitySearchOptions): EntitySearchResult {
  const { page = 0, rowsPerPage = 25, enabled = true } = options
  const account = useAccount()
  const entity = buildEntitySearchQuery(options)

  const query = useQuery<EntityListResponse, Error>({
    // The whole query object is in the key, so every filter reaches it. A hook
    // that keyed on only some of them would answer a filtered view out of the
    // unfiltered entry — a 200 with plausible rows and no error, on a screen
    // whose next action is OVERWRITING one of those rows.
    queryKey: ['sky_entity_search', { entity, page, rowsPerPage }],
    enabled: Boolean(account.user) && enabled,
    staleTime: 15000,
    queryFn: async () => {
      try {
        const res = await axios.post<EntityListResponse>(skyJobURL('/entities/query'), {
          entity,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        })
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  return {
    entities: query.data?.entities ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as Error | null,
    refetch: () => query.refetch(),
  }
}

/** The react-query key for one entity's detail, shared with the mutation. */
export const entityDetailKey = (id: string) => ['sky_entity_detail', id] as const

export interface EntityDetailResult {
  detail: EntityDetailResponse | undefined
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * Everything one post currently says: its columns, its labels, and its
 * per-entity metric values with the catalog's display metadata attached.
 *
 * Nothing else answers this. `/entities/metrics/query` AGGREGATES across a
 * population — a sum or an average is the dashboard's number, not "what does
 * this post say right now", which is the only question an editor can open with.
 *
 * `undefined` id is the ordinary state of the page before anything is selected,
 * so it asks for nothing rather than erroring.
 */
export function useEntityDetail(id: string | undefined): EntityDetailResult {
  const account = useAccount()

  const query = useQuery<EntityDetailResponse, Error>({
    queryKey: entityDetailKey(id ?? ''),
    enabled: Boolean(account.user) && Boolean(id),
    // Deliberately NOT cached for long: this is the "before" of an overwrite,
    // and a stale copy is how two people's edits silently disagree.
    staleTime: 0,
    queryFn: async () => {
      try {
        const res = await axios.get<EntityDetailResponse>(
          skyJobURL(`/entities/${encodeURIComponent(id as string)}`),
        )
        return res.data
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  return {
    detail: query.data,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: () => query.refetch(),
  }
}

export interface EntityPatchVariables {
  id: string
  /** Built by `buildEntityPatch` — sent VERBATIM. */
  patch: EntityPatchRequest
}

/**
 * Apply one hand correction.
 *
 * 🔴 THE BODY IS PASSED THROUGH UNTOUCHED. The merge semantics live in
 * `buildEntityPatch` (components/social-media/entityEdits.ts), and a hook that
 * "helpfully" pruned nulls on the way past would turn every deletion into a
 * silent no-op — the user clears a field, the save succeeds, and the number is
 * still there when the page reloads.
 *
 * The response is the full detail shape AFTER the merge, so it is written
 * straight back into the detail cache: the editor then re-renders from the
 * stored truth rather than from what it hoped it wrote, which is what makes
 * somebody else's concurrent edit visible instead of invisible.
 */
export function useEntityPatch() {
  const queryClient = useQueryClient()

  return useMutation<EntityDetailResponse, Error, EntityPatchVariables>({
    mutationFn: async ({ id, patch }) => {
      try {
        const res = await axios.patch<EntityDetailResponse>(
          skyJobURL(`/entities/${encodeURIComponent(id)}`),
          patch,
        )
        return res.data
      } catch (error) {
        // The server's 400 names the offending key ("label \"u35\" must be a
        // string…"). That sentence is the only thing on screen that tells the
        // user what to do, so it must not be flattened into "request failed".
        throw new Error(extractErrorMessage(error))
      }
    },
    onSuccess: (data, { id }) => {
      queryClient.setQueryData(entityDetailKey(id), data)
      // A label edit can change what the row says, so the list it came from is
      // no longer known-good.
      queryClient.invalidateQueries({ queryKey: ['sky_entity_search'] })
    },
  })
}
